// Development logging (Milestone 7.3, brief §18).
//
// Logs endpoint, method, status, duration and correlation id, plus - when
// `payloads` is on - the request body and response body. Those two are what
// make a log line useful for checking the client against the API contract:
// you can read the actual JSON that went over the wire.
//
// Credentials never appear, but this class is not what guarantees that:
// redaction happens in the TRANSPORT, before a body is handed to any logger.
// That placement matters because the logger is injectable - masking here would
// protect only this implementation and not a host application's own logger.
//
// Disabled by default (see apiDefaults) so production is silent unless a
// publisher explicitly opts in.
export class ApiLogger {
  #enabled;
  #payloads;
  #sink;

  constructor({ enabled = false, payloads = true, logger = null } = {}) {
    this.#enabled = enabled;
    this.#payloads = payloads;
    this.#sink = logger ?? console;
  }

  /**
   * @param {object} entry
   * @param {object} [entry.body]      The request body that was sent.
   * @param {object} [entry.response]  The parsed response body.
   */
  request({ method, path, status, durationMs, requestId, body, response }) {
    if (!this.#enabled) return;

    const id = requestId ? ` [${requestId}]` : '';
    this.#sink.log?.(`[PlayBid API] ${method} ${path} -> ${status} (${durationMs}ms)${id}`);

    if (!this.#payloads) return;
    if (body !== undefined && body !== null) {
      this.#sink.log?.('  ↳ request ', body);
    }
    if (response !== undefined && response !== null) {
      this.#sink.log?.('  ↳ response', response);
    }
  }

  // Telemetry failures are expected and non-actionable for a publisher, so
  // they are logged at debug volume and never as errors.
  telemetryFailure(endpoint, error) {
    if (!this.#enabled) return;
    this.#sink.warn?.(
      `[PlayBid API] fire-and-forget ${endpoint} failed: ${error?.code ?? 'unknown'}`,
    );
  }
}
